import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Box,
  CircularProgress,
  Link,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConversations,
  fetchTranscripts,
  fetchSingleConversation,
  FetchConversationsParams,
} from "../../redux/actions/conversationsActions";
import { RootState, AppDispatch } from "../../redux/store";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { debounce } from "lodash";

interface ConversationsTabProps {
  userId?: string;
  canReadConversation?: boolean;
}
const ConversationsTab: React.FC<ConversationsTabProps> = ({
  userId,
  canReadConversation,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const {
    conversations,
    transcripts,
    loading,
    error,
    loadingAllTranscripts,
    loadingMore,
    hasMoreEntries,
  } = useSelector((state: RootState) => state.conversations);
  const navigate = useNavigate();

  // Get the current user from the Redux state if userId is provided
  const cursorTimestamp = useSelector(
    (state: RootState) => state.conversations.cursor
  );
  // Filter states
  const [conversationIdFilter, setConversationIdFilter] = useState<string>("");
  const [environmentFilter, setEnvironmentFilter] = useState<string>("");
  const [removePrivateFilter, setRemovePrivateFilter] =
    useState<boolean>(false);
  const [minTurnsFilter, setMinTurnsFilter] = useState<number | null>(null);

  // Pagination
  const PAGE_SIZE = 50;

  const fetchConversationsWithParams = useCallback(
    (params: FetchConversationsParams = {}) => {
      // Include all filter parameters in the API call
      const fetchParams: FetchConversationsParams = {
        ...params,
        limit: params.limit || PAGE_SIZE,
        conversationId: conversationIdFilter || undefined,
        environment: environmentFilter || undefined,
        removePrivate: removePrivateFilter || undefined,
        minTurns: minTurnsFilter || undefined,
        userId: userId || undefined, // Include userId if provided
        reset: params.reset,
      };

      dispatch(fetchConversations(fetchParams));
    },
    [
      dispatch,
      conversationIdFilter,
      environmentFilter,
      removePrivateFilter,
      minTurnsFilter,
      userId, // Add userId as a dependency
    ]
  );

  const debouncedFetchConversations = useMemo(
    () => debounce(() => fetchConversationsWithParams({ reset: true }), 300),
    [fetchConversationsWithParams]
  );

  const debouncedFetchTranscripts = useMemo(
    () => debounce(() => dispatch(fetchTranscripts()), 300),
    [dispatch]
  );

  const pageNumber = useSelector(
    (state: RootState) => state.conversations.page_number
  );

  const handleLoadMore = useCallback(() => {
    if (hasMoreEntries && !loadingMore) {
      fetchConversationsWithParams({
        page_number: pageNumber + 1,
        limit: PAGE_SIZE,
      });
    }
  }, [hasMoreEntries, loadingMore, pageNumber, fetchConversationsWithParams]);

  useEffect(() => {
    if (canReadConversation) {
      debouncedFetchConversations();
    } else {
      debouncedFetchTranscripts();
    }

    // Clean up the debounced functions on unmount
    return () => {
      debouncedFetchConversations.cancel();
      debouncedFetchTranscripts.cancel();
    };
  }, [
    debouncedFetchConversations,
    debouncedFetchTranscripts,
    canReadConversation,
  ]);

  // For transcripts, we still need to filter locally
  const filteredRows = useMemo(() => {
    if (canReadConversation) {
      // For conversations, we're using the backend-filtered data
      // No need to filter by userId on the frontend anymore as it's done on the backend
      return [...conversations];
    } else {
      // For transcripts, continue to use frontend filtering
      let filteredData = [...transcripts];

      // Apply filters
      if (conversationIdFilter) {
        filteredData = filteredData.filter((item: any) =>
          item.conversation_id?.includes(conversationIdFilter)
        );
      }

      if (environmentFilter) {
        filteredData = filteredData.filter(
          (item: any) => item.environment === environmentFilter
        );
      }

      if (removePrivateFilter) {
        filteredData = filteredData.filter((item: any) => !item.is_private);
      }

      if (minTurnsFilter !== null) {
        filteredData = filteredData.filter(
          (item: any) =>
            item.num_turns_in_conversation >= (minTurnsFilter as number)
        );
      }

      const sortedData = [...filteredData].sort((a, b) => {
        const dateA = a.latest_chat_message_time
          ? new Date(a.latest_chat_message_time)
          : new Date(0);
        const dateB = b.latest_chat_message_time
          ? new Date(b.latest_chat_message_time)
          : new Date(0);
        return dateB.getTime() - dateA.getTime(); // For descending order
      });

      return sortedData;
    }
  }, [
    conversations,
    transcripts,
    canReadConversation,
    conversationIdFilter,
    environmentFilter,
    removePrivateFilter,
    minTurnsFilter,
  ]);

  // State to track direct navigation loading
  const [directNavigationLoading, setDirectNavigationLoading] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const conversationId = queryParams.get("conversation_id");
    const targetId = queryParams.get("target_id"); // Extract the target_id

    if (conversationId && !directNavigationLoading && filteredRows.length > 0) {
      // Check if the conversation is in the current list
      const exists = filteredRows.some((row) => {
        if ("id" in row) {
          return row.id === conversationId;
        } else if ("conversation_id" in row) {
          return row.conversation_id === conversationId;
        }
        return false;
      });

      if (!exists && canReadConversation) {
        // If not in the current list, fetch it directly
        setDirectNavigationLoading(true);

        dispatch(fetchSingleConversation(conversationId))
          .unwrap()
          .then(() => {
            // Then navigate to the conversation view
            const url = targetId
              ? `/dashboard/conversations/${conversationId}?target_id=${targetId}`
              : `/dashboard/conversations/${conversationId}`;
            navigate(url);
          })
          .catch((error) => {
            console.error("Failed to fetch conversation:", error);
            // Optionally show an error message to the user
          })
          .finally(() => {
            setDirectNavigationLoading(false);
          });
      } else {
        // If it's in the list or we can't fetch directly, navigate as usual
        const url = targetId
          ? `/dashboard/conversations/${conversationId}?target_id=${targetId}`
          : `/dashboard/conversations/${conversationId}`;
        navigate(url);
      }
    }
  }, [
    filteredRows,
    location.search,
    navigate,
    dispatch,
    directNavigationLoading,
    canReadConversation,
  ]);

  const handleViewConversation = useCallback(
    (id: string) => {
      window.open(`/dashboard/conversations/${id}`, "_blank");
    },
    [] // No dependencies
  );

  const renderBooleanCell = (params: any) => (
    <p style={{ textAlign: "center", margin: 0 }}>
      {params.value ? "✔️" : "❌"}
    </p>
  );

  const columns: GridColDef[] = useMemo(() => {
    return canReadConversation
      ? [
          {
            field: "id",
            headerName: "Conversation ID",
            flex: 1.5,
            minWidth: 200,
            headerAlign: "center",
            align: "center",
            sortable: false,
          },
          {
            field: "owning_user_id",
            headerName: "User ID",
            flex: 0.5,
            minWidth: 100,
            headerAlign: "center",
            align: "center",
            sortable: false,
            renderCell: (params: any) =>
              params.value ? (
                <Link
                  href={`/dashboard/users?user=${params.value}`}
                  style={{ textDecoration: "none", color: "blue" }}
                >
                  {params.value}
                </Link>
              ) : null,
          },
          {
            field: "created_at",
            headerName: "Created At",
            flex: 0.5,
            minWidth: 150,
            headerAlign: "center",
            align: "center",
            sortable: false,
            valueGetter: (params: any) => {
              return params
                ? format(new Date(params), "yyyy-MM-dd HH:mm:ss")
                : null;
            },
          },
          {
            field: "latest_chat_message_time",
            headerName: "Last Chat Message",
            flex: 1,
            minWidth: 150,
            headerAlign: "center",
            align: "center",
            sortable: false,
            valueGetter: (params: any) => {
              return params
                ? format(new Date(params), "yyyy-MM-dd HH:mm:ss")
                : null;
            },
          },
          {
            field: "num_turns_in_conversation",
            headerName: "Chat Turns",
            flex: 0.5,
            minWidth: 120,
            headerAlign: "center",
            align: "center",
            sortable: false,
          },
          {
            field: "is_private",
            headerName: "Is Private?",
            flex: 0.5,
            renderCell: renderBooleanCell,
            minWidth: 100,
            headerAlign: "center",
            align: "center",
            sortable: false,
          },
          {
            field: "environment",
            headerName: "Environment",
            flex: 0.5,
            minWidth: 100,
            headerAlign: "center",
            align: "center",
            sortable: false,
          },
          {
            field: "actions",
            headerName: "",
            sortable: false,
            disableColumnMenu: true,
            flex: 0.5,
            minWidth: 150,
            headerAlign: "center",
            // Render our CreateConversationDialog in the header
            renderHeader: () => {
              return <span>Actions</span>;
            },
            renderCell: (params: any) => (
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleViewConversation(params.row.id)}
              >
                View
              </Button>
            ),
            align: "center",
          },
        ]
      : [
          {
            field: "conversation_id",
            headerName: "Conversation ID",
            flex: 1,
            minWidth: 130,
            headerAlign: "center",
            align: "center",
            sortable: false,
          },
          {
            field: "created_at",
            headerName: "Created At",
            flex: 1,
            minWidth: 150,
            headerAlign: "center",
            align: "center",
            sortable: false,
            valueGetter: (params: any) => {
              return params
                ? format(new Date(params), "yyyy-MM-dd HH:mm:ss")
                : null;
            },
          },
          {
            field: "human_initial",
            headerName: "Human Initial",
            flex: 0.33,
            minWidth: 50,
            headerAlign: "center",
            align: "center",
            sortable: false,
          },
          {
            field: "latest_chat_message_time",
            headerName: "Last Chat Message",
            flex: 1,
            minWidth: 150,
            headerAlign: "center",
            align: "center",
            sortable: false,
            valueGetter: (params: any) => {
              return params
                ? format(new Date(params), "yyyy-MM-dd HH:mm:ss")
                : null;
            },
          },
          {
            field: "num_turns_in_conversation",
            headerName: "Chat Turns",
            flex: 0.5,
            minWidth: 120,
            headerAlign: "center",
            align: "center",
            sortable: false,
          },
          {
            field: "actions",
            headerName: "",
            flex: 0.5,
            minWidth: 150,
            headerAlign: "center",
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: any) => (
              <p style={{ textAlign: "center", margin: 0 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    handleViewConversation(params.row.conversation_id)
                  }
                >
                  View
                </Button>
              </p>
            ),
          },
        ];
  }, [canReadConversation, handleViewConversation]);

  // Show error if there is one
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mt: 2,
          width: "100%",
        }}
      >
        {canReadConversation && !userId ? (
          <>
            {/* Conversation ID Filter */}
            <TextField
              label="Conversation ID"
              variant="outlined"
              value={conversationIdFilter}
              onChange={(e) => {
                setConversationIdFilter(e.target.value);
                debouncedFetchConversations();
              }}
              sx={{ flex: 1 }}
            />

            {/* Environment Filter */}
            <FormControl variant="outlined" sx={{ minWidth: 150 }}>
              <InputLabel>Environment</InputLabel>
              <Select
                value={environmentFilter}
                onChange={(e) => {
                  setEnvironmentFilter(e.target.value);
                  debouncedFetchConversations();
                }}
                label="Environment"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                <MenuItem value="qa">QA</MenuItem>
                <MenuItem value="staging">Staging</MenuItem>
                <MenuItem value="demo">Demo</MenuItem>
                <MenuItem value="production">Production</MenuItem>
                {/* Add other environments as needed */}
              </Select>
            </FormControl>

            {/* Minimum Turns Filter */}
            <TextField
              label="Min Turns in Conversation"
              variant="outlined"
              type="number"
              value={minTurnsFilter !== null ? minTurnsFilter : ""}
              onChange={(e) => {
                setMinTurnsFilter(
                  e.target.value ? parseInt(e.target.value, 10) : null
                );
                debouncedFetchConversations();
              }}
              sx={{ width: "200px" }}
            />
            {/* Remove Private Filter */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={removePrivateFilter}
                  onChange={(e) => {
                    setRemovePrivateFilter(e.target.checked);
                    debouncedFetchConversations();
                  }}
                />
              }
              label="Remove Private"
            />
          </>
        ) : null}
        {!canReadConversation && !userId && (
          <>
            {/* Conversation ID Filter */}
            <TextField
              label="Conversation ID"
              variant="outlined"
              value={conversationIdFilter}
              onChange={(e) => setConversationIdFilter(e.target.value)}
              sx={{ flex: 1 }}
            />

            {/* Additional filters can be added here for non-admin users */}
          </>
        )}
        {!userId && (
          <Button
            variant="outlined"
            onClick={() => {
              setRemovePrivateFilter(false);
              setMinTurnsFilter(null);
              setEnvironmentFilter("");
              setConversationIdFilter(""); // Reset status filter

              // Fetch with reset filters
              fetchConversationsWithParams({ reset: true });
            }}
          >
            Reset Filters
          </Button>
        )}
      </Box>

      {userId && (
        <Box sx={{ paddingBottom: "16px" }}>
          <Typography variant="h6" gutterBottom>
            User Conversations
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          height: userId ? "30vh" : "65vh",
          width: "100%",
          mt: 2,
          position: "relative",
        }}
      >
        {(loading || loadingAllTranscripts) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id || row.conversation_id}
        />
      </Box>
      {/* Load more button for conversations */}
      {canReadConversation && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {cursorTimestamp && (
            <Typography variant="caption" color="text.secondary">
              Data as of&nbsp;
              {format(new Date(cursorTimestamp), "yyyy‑MM‑dd HH:mm:ss")}
            </Typography>
          )}

          {hasMoreEntries && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? <CircularProgress size={24} /> : "Load More"}
            </Button>
          )}
        </Box>
      )}
    </>
  );
};

export default ConversationsTab;
